import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Clock, Loader2, LogOut } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Badge } from '@/app/components/ui/badge';
import { Skeleton } from '@/app/components/ui/skeleton';
import {
  useOnSiteTechs,
  useCreateCheckIn,
  useCheckOut,
  useCheckIns,
  useTechProfiles,
} from '@/hooks/use-crew';
import { useOrgTimeZone } from '@/hooks/use-settings';
import { formatDateTimeInTimeZone } from '@/lib/date-utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/app/components/ui/dialog';
import { handleApiError } from '@/lib/api-client';
import {
  cacheSigningToken,
  removeCachedSigningTokensForCheckIn,
} from '@/lib/offline/shift-signing-token-cache';

export function CheckInMapPage() {
  const timeZone = useOrgTimeZone();
  const { data: onSite, isLoading } = useOnSiteTechs();
  const { data: recentCheckIns } = useCheckIns({ active: false });
  const { data: techsList } = useTechProfiles({ page_size: 200, status: 'active' });
  const createCheckIn = useCreateCheckIn();
  const checkOutMutation = useCheckOut();

  const [open, setOpen] = useState(false);
  const [selectedTech, setSelectedTech] = useState('');
  const [locationName, setLocationName] = useState('');
  const [coords, setCoords] = useState<{ lat: string; lng: string } | null>(null);
  const [geoError, setGeoError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && !coords) {
      if ('geolocation' in navigator) {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            setCoords({
              lat: pos.coords.latitude.toFixed(6),
              lng: pos.coords.longitude.toFixed(6),
            });
            setGeoError(null);
          },
          (err) => {
            setGeoError(err.message);
          }
        );
      } else {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setGeoError('Geolocation is not supported by this browser.');
      }
    }
  }, [open, coords]);

  const handleCheckIn = async () => {
    setError(null);
    if (!selectedTech) {
      setError('Please select a technician.');
      return;
    }
    if (!coords) {
      setError('GPS coordinates not available.');
      return;
    }
    try {
      const data = await createCheckIn.mutateAsync({
        tech: Number(selectedTech),
        latitude: coords.lat,
        longitude: coords.lng,
        check_in_time: new Date().toISOString(),
        location_name: locationName || undefined,
      });
      if (data.signing_token && data.tech_user_username) {
        await cacheSigningToken({
          checkInId: data.id,
          username: data.tech_user_username,
          token: data.signing_token,
          expiresAt: data.signing_token_expires_at ?? null,
        });
      }
      setOpen(false);
      setSelectedTech('');
      setLocationName('');
      setCoords(null);
    } catch (err) {
      setError(handleApiError(err).message);
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-center gap-4">
        <Button asChild variant="ghost" size="icon">
          <Link to="/crew">
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">GPS Check-In</h1>
          <p className="text-gray-600">Track technician locations</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button>
              <MapPin className="h-4 w-4 mr-1" />
              New Check-In
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Check In</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {error && <div className="bg-red-50 text-red-700 p-2 rounded text-sm">{error}</div>}
              <div>
                <Label>Technician</Label>
                <Select value={selectedTech} onValueChange={setSelectedTech}>
                  <SelectTrigger><SelectValue placeholder="Select tech..." /></SelectTrigger>
                  <SelectContent>
                    {techsList?.results.map((t) => (
                      <SelectItem key={t.id} value={String(t.id)}>
                        {t.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Location Name (optional)</Label>
                <Input
                  value={locationName}
                  onChange={(e) => setLocationName(e.target.value)}
                  placeholder="e.g. Site A - Tower 3"
                />
              </div>
              <div className="bg-gray-50 p-3 rounded text-sm">
                {geoError ? (
                  <p className="text-red-600">GPS Error: {geoError}</p>
                ) : coords ? (
                  <p className="text-gray-700">
                    <MapPin className="h-3 w-3 inline mr-1" />
                    {coords.lat}, {coords.lng}
                  </p>
                ) : (
                  <p className="text-gray-500">
                    <Loader2 className="h-3 w-3 inline mr-1 animate-spin" />
                    Acquiring GPS...
                  </p>
                )}
              </div>
              <Button onClick={handleCheckIn} disabled={createCheckIn.isPending} className="w-full">
                {createCheckIn.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                Check In
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-green-500" />
            Currently On Site ({onSite?.length ?? 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(!onSite || onSite.length === 0) ? (
            <p className="text-gray-500 text-sm">No technicians currently checked in.</p>
          ) : (
            <div className="space-y-3">
              {onSite.map((ci) => (
                <div
                  key={ci.id}
                  className="flex items-center justify-between p-3 bg-green-50 rounded-lg"
                >
                  <div>
                    <p className="font-medium">{ci.tech_name}</p>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <MapPin className="h-3 w-3" />
                      {ci.location_name || `${ci.latitude}, ${ci.longitude}`}
                    </div>
                    <div className="flex items-center gap-1 text-xs text-gray-500 mt-1">
                      <Clock className="h-3 w-3" />
                      Since {formatDateTimeInTimeZone(ci.check_in_time, timeZone)}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      checkOutMutation.mutate(ci.id, {
                        onSuccess: (data) => {
                          if (data?.id) {
                            removeCachedSigningTokensForCheckIn(data.id);
                          }
                        },
                      })
                    }
                    disabled={checkOutMutation.isPending}
                  >
                    <LogOut className="h-3 w-3 mr-1" />
                    Check Out
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {recentCheckIns && recentCheckIns.results.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-gray-500" />
              Recent Check-Ins
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {recentCheckIns.results.slice(0, 10).map((ci) => (
                <div
                  key={ci.id}
                  className="flex items-center justify-between p-2 rounded border"
                >
                  <div>
                    <p className="text-sm font-medium">{ci.tech_name}</p>
                    <p className="text-xs text-gray-500">
                      {ci.location_name || `${ci.latitude}, ${ci.longitude}`}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500">
                      {formatDateTimeInTimeZone(ci.check_in_time, timeZone, { dateStyle: 'medium' })}
                    </p>
                    <Badge
                      className={ci.check_out_time ? 'bg-gray-100 text-gray-700' : 'bg-green-100 text-green-700'}
                    >
                      {ci.check_out_time ? 'Checked Out' : 'On Site'}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
